import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { ProviderComponent } from './provider/provider.component';
import { AssetsComponent } from './assets/assets.component';
import { OrganisationComponent } from './organisation/organisation.component';
import { LeadershipApiService, LeadershipMember as ApiLeadershipMember } from '@angular-workspace/services';

export interface LeadershipMember {
  id: number;
  rank: number;
  name: string;
  role: string;
  title: string;
  bio: string;
  image?: string;
  email?: string;
  linkedin?: string;
  expertise: string[];
  score?: number; // Optional score for competition feel
  // Additional fields from API
  publishedDatabank?: number;
  publishedAiModels?: number;
  publishedUsecases?: number;
  totalPublished?: number;
  downloads?: number;
  likes?: number;
  dislikes?: number;
  organizationId?: string;
  organizationName?: string;
  assetType?: string; // Asset type for Assets category (e.g., "Model", "Dataset", "Usecase")
  views?: number; // Number of views for assets
  members?: number; // Total number of members (for Organisation category)
}

export type DesignType = 'podium' | 'grid' | 'timeline' | 'magazine' | 'minimal';
export type LeadershipCategory = 'providers' | 'assets' | 'organisation';

@Component({
  selector: 'app-leadership',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AssetHeaderComponent,
    FooterComponent,
    ProviderComponent,
    AssetsComponent,
    OrganisationComponent
  ],
  templateUrl: './leadership.component.html',
  styleUrl: './leadership.component.scss',
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px) scale(0.9)' }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ])
    ])
  ]
})
export class LeadershipComponent implements OnInit {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Leaderboard' }
    ],
    title: 'Leaderboard',
    description: 'Meet the visionary leaders driving innovation in agricultural technology and data science at MahaAgX'
  };

  // Active category tab
  activeCategory: LeadershipCategory = 'providers';

  // Category tabs configuration
  categoryTabs = [
    {
      key: 'providers' as LeadershipCategory,
      label: 'Providers',
      icon: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path>'
    },
    {
      key: 'assets' as LeadershipCategory,
      label: 'Assets',
      icon: '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line>'
    },
    {
      key: 'organisation' as LeadershipCategory,
      label: 'Organisation',
      icon: '<rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>'
    }
  ];

  leadershipMembers: LeadershipMember[] = [];
  
  // Pagination state for Assets
  assetsPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };

  // Pagination state for Providers
  providersPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };

  // Pagination state for Organisation
  organisationPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };
  
  // Mock data (kept for reference, not used when API is available)
  mockLeadershipMembers: LeadershipMember[] = [
    {
      id: 1,
      rank: 1,
      name: 'Dr. Rajesh Kumar',
      role: 'Chief Executive Officer',
      title: 'CEO',
      bio: '',
      expertise: ['Strategic Planning', 'Digital Agriculture', 'Policy Development'],
      email: 'rajesh.kumar@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/rajeshkumar',
      score: 98
    },
    {
      id: 2,
      rank: 2,
      name: 'Dr. Priya Sharma',
      role: 'Chief Technology Officer',
      title: 'CTO',
      bio: 'Technology innovator specializing in AI/ML applications for agriculture. Dr. Sharma leads the development of MahaAgX\'s cutting-edge data platform and AI model ecosystem.',
      expertise: ['AI/ML', 'Data Engineering', 'Cloud Infrastructure'],
      email: 'priya.sharma@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/priyasharma',
      score: 95
    },
    {
      id: 3,
      rank: 3,
      name: 'Dr. Anil Patel',
      role: 'Chief Data Officer',
      title: 'CDO',
      bio: 'Data science expert with a passion for making agricultural data accessible and actionable. Dr. Patel oversees data governance, quality, and strategic data initiatives.',
      expertise: ['Data Science', 'Data Governance', 'Analytics'],
      email: 'anil.patel@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/anilpatel',
      score: 92
    },
    {
      id: 4,
      rank: 4,
      name: 'Ms. Meera Desai',
      role: 'Head of Partnerships',
      title: 'Partnerships Lead',
      bio: 'Strategic partnership builder connecting farmers, researchers, and technology providers. Meera has established key collaborations that drive innovation across the agricultural ecosystem.',
      expertise: ['Partnership Development', 'Ecosystem Building', 'Stakeholder Management'],
      email: 'meera.desai@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/meeradesai',
      score: 89
    },
    {
      id: 5,
      rank: 5,
      name: 'Dr. Vikram Singh',
      role: 'Head of Research & Innovation',
      title: 'Research Lead',
      bio: 'Agricultural research scientist leading innovative projects that bridge the gap between research and practical farming solutions. Dr. Singh has published extensively on sustainable agriculture.',
      expertise: ['Agricultural Research', 'Innovation', 'Sustainability'],
      email: 'vikram.singh@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/vikramsingh',
      score: 87
    },
    {
      id: 6,
      rank: 6,
      name: 'Mr. Suresh Reddy',
      role: 'Head of Platform Operations',
      title: 'Operations Lead',
      bio: 'Operations expert ensuring seamless platform functionality and user experience. Suresh manages infrastructure, security, and platform reliability for thousands of users.',
      expertise: ['Platform Operations', 'DevOps', 'Security'],
      email: 'suresh.reddy@mahaagx.gov.in',
      linkedin: 'https://linkedin.com/in/sureshreddy',
      score: 85
    }
  ];

  get topThree(): LeadershipMember[] {
    return this.leadershipMembers.filter(m => m.rank <= 3).sort((a, b) => a.rank - b.rank);
  }

  get others(): LeadershipMember[] {
    return this.leadershipMembers.filter(m => m.rank > 3).sort((a, b) => a.rank - b.rank);
  }

  selectedDesign: DesignType = 'magazine';

  designOptions: Array<{ value: DesignType; label: string; icon: string }> = [
    { value: 'podium', label: 'Podium', icon: '🏆' },
    { value: 'grid', label: 'Grid', icon: '📊' },
    { value: 'timeline', label: 'Timeline', icon: '📅' },
    { value: 'magazine', label: 'Magazine', icon: '📰' },
    { value: 'minimal', label: 'Minimal', icon: '✨' }
  ];

  private leadershipApiService = inject(LeadershipApiService);

  constructor() {}

  async ngOnInit(): Promise<void> {
    console.log('LeadershipComponent ngOnInit called');
    // Load providers leadership data on page load (default category)
    await this.loadProvidersLeadership(1);
  }

  /**
   * Load leadership data for providers
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadProvidersLeadership(page: number = 1): Promise<void> {
    try {
      if (this.providersPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      this.providersPagination.isLoading = true;
      console.log('Loading providers leadership data for page:', page);
      const response = await this.leadershipApiService.getLeadershipForProvider(page, 10);
      
      // Map API response to component's LeadershipMember format
      // API response structure: { type, title, result: [...] } or array directly
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.providersPagination.hasNext = paginationInfo.hasNext || false;
          this.providersPagination.totalCount = paginationInfo.totalCount || 0;
          this.providersPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => {
          const organizationName = this.toDisplayText(
            member.organizationName ||
            member.organisationName ||
            member.organisation_name
          );
          const username = this.toDisplayText(
            member.username ||
            member.userName ||
            member.user_name
          );
          const providerName = username || organizationName;

          return {
          id: member.rank || 0, // Use rank as ID
          rank: member.rank || 0,
          name: providerName,
          role: organizationName,
          title: organizationName || providerName,
          bio: '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0, // Use totalPublished for Published metric
          // Additional fields from API
          publishedDatabank: member.publishedDatabank || member.publishedDatabanks || 0,
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.organizationId || member.id,
          organizationName,
          members: member.members || 0 // Number of members (for Provider/Organisation)
        };
        });
        
        if (page === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.providersPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading providers leadership:', error);
      this.providersPagination.isLoading = false;
    }
  }

  changeDesign(design: DesignType): void {
    this.selectedDesign = design;
  }

  async changeCategory(category: LeadershipCategory): Promise<void> {
    this.activeCategory = category;
    console.log('Category changed to:', category);
    
    // Reset pagination when switching categories
    if (category === 'assets') {
      this.assetsPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    } else if (category === 'providers') {
      this.providersPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    } else if (category === 'organisation') {
      this.organisationPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    }
    
    // Load leadership data based on selected category
    switch (category) {
      case 'providers':
        await this.loadProvidersLeadership(1);
        break;
      case 'assets':
        await this.loadAssetsLeadership(1);
        break;
      case 'organisation':
        await this.loadOrganisationLeadership(1);
        break;
    }
  }

  /**
   * Load leadership data for assets
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadAssetsLeadership(page: number = 1): Promise<void> {
    try {
      if (this.assetsPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      // Set loading state BEFORE the API call
      this.assetsPagination.isLoading = true;
      console.log('Loading assets leadership data for page:', page);
      
      const response = await this.leadershipApiService.getLeaderboardForAssets(page, 10);
      
      // Map API response to component's LeadershipMember format
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.assetsPagination.hasNext = paginationInfo.hasNext || false;
          this.assetsPagination.totalCount = paginationInfo.totalCount || 0;
          this.assetsPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => ({
          id: member.id || member.rank || 0,
          rank: member.rank || 0,
          name: member.name || member.organizationName || '', // Asset name from API
          role: member.organizationName || '', // Organization name displayed below asset name
          title: member.organizationName || '',
          bio: member.shortDescription || '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0,
          publishedDatabank: member.publishedDatabank || member.publishedDatabanks || 0,
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.organizationId || member.id,
          organizationName: member.organizationName,
          assetType: this.formatAssetType(member.type) // Format asset type from API (e.g., "DATABANK", "AI_MODEL")
        }));
        
        if (this.assetsPagination.currentPage === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.assetsPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading assets leadership:', error);
      this.assetsPagination.isLoading = false;
    }
  }

  /**
   * Load more assets (next page)
   */
  async loadMoreAssets(): Promise<void> {
    if (this.assetsPagination.hasNext && !this.assetsPagination.isLoading) {
      const nextPage = this.assetsPagination.currentPage + 1;
      await this.loadAssetsLeadership(nextPage);
    }
  }

  /**
   * Load more providers (next page)
   */
  async loadMoreProviders(): Promise<void> {
    if (this.providersPagination.hasNext && !this.providersPagination.isLoading) {
      const nextPage = this.providersPagination.currentPage + 1;
      await this.loadProvidersLeadership(nextPage);
    }
  }

  /**
   * Load more organisations (next page)
   */
  async loadMoreOrganisations(): Promise<void> {
    if (this.organisationPagination.hasNext && !this.organisationPagination.isLoading) {
      const nextPage = this.organisationPagination.currentPage + 1;
      await this.loadOrganisationLeadership(nextPage);
    }
  }

  /**
   * Load leadership data for organisation
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadOrganisationLeadership(page: number = 1): Promise<void> {
    try {
      if (this.organisationPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      this.organisationPagination.isLoading = true;
      console.log('Loading organisation leadership data for page:', page);
      const response = await this.leadershipApiService.getLeaderboardForOrganisations(page, 10);
      
      // Map API response to component's LeadershipMember format
      // Organisation API returns array directly or wrapped in result
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.organisationPagination.hasNext = paginationInfo.hasNext || false;
          this.organisationPagination.totalCount = paginationInfo.totalCount || 0;
          this.organisationPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => ({
          id: member.rank || 0,
          rank: member.rank || 0,
          name: member.organizationName || '', // Organisation uses organizationName as name
          role: member.organizationName || '', // Display organization name below username
          title: member.organizationName || '',
          bio: '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0, // Use totalPublished for Published metric
          publishedDatabank: member.publishedDatabanks || member.publishedDatabank || 0, // Note: API uses publishedDatabanks (plural)
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.id, // Organisation API uses 'id' for organizationId
          organizationName: member.organizationName,
          members: member.members || 0 // Number of members in the organization
        }));
        
        if (page === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.organisationPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading organisation leadership:', error);
      this.organisationPagination.isLoading = false;
    }
  }

  private toDisplayText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    if (!text) {
      return '';
    }
    const lower = text.toLowerCase();
    if (lower === 'undefined' || lower === 'null' || lower === 'nan' || lower === 'n/a') {
      return '';
    }
    return text;
  }

  /**
   * Format asset type from API format to display format
   * @param assetType Raw asset type from API
   * @returns Formatted asset type (e.g., "Model", "Dataset", "Usecase")
   */
  private formatAssetType(assetType: string | string[] | undefined): string {
    if (!assetType) return 'N/A';
    
    // Handle array of types (take first one)
    let typeStr = '';
    if (Array.isArray(assetType) && assetType.length > 0) {
      typeStr = assetType[0];
    } else if (typeof assetType === 'string') {
      typeStr = assetType;
    } else {
      return 'N/A';
    }
    
    // Extract type name after colon (e.g., "adex:Apps" -> "Apps")
    const parts = typeStr.split(':');
    let typeName = parts.length > 1 ? parts[parts.length - 1] : typeStr;
    
    // Normalize case for comparison
    const normalized = typeName.toLowerCase().replace(/[\s_-]/g, '');
    
    // Map to display format
    if (normalized === 'aimodel' || normalized === 'ai_model' || normalized === 'model') {
      return 'Model';
    } else if (normalized === 'databank' || normalized === 'data_bank' || normalized === 'dataset') {
      return 'Dataset';
    } else if (normalized === 'apps' || normalized === 'app' || normalized === 'usecase' || normalized === 'use_case') {
      return 'Use Case';
    }
    
    // Handle uppercase API formats (e.g., "DATABANK", "AI_MODEL")
    const upperNormalized = typeName.toUpperCase();
    if (upperNormalized === 'DATABANK' || upperNormalized === 'DATA_BANK') {
      return 'Dataset';
    } else if (upperNormalized === 'AI_MODEL' || upperNormalized === 'AIMODEL') {
      return 'Model';
    } else if (upperNormalized === 'APPS' || upperNormalized === 'USECASE' || upperNormalized === 'USE_CASE') {
      return 'Use Case';
    }
    
    // Fallback: capitalize first letter
    return typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase();
  }
}
